import { useState, useEffect, useCallback } from 'react';
import { X, Bell } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { onForegroundMessage } from '../utils/messaging';
import { addPushListeners } from '../utils/capacitorPush';
import { useAuth } from '../contexts/AuthContext';

interface ToastData {
  id: string;
  title: string;
  body: string;
  clickAction: string;
}

export default function NotificationToast() {
  const { currentUser } = useAuth();
  const [toasts, setToasts] = useState<ToastData[]>([]);
  const navigate = useNavigate();

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Web foreground messages
  useEffect(() => {
    if (!currentUser || Capacitor.isNativePlatform()) return;
    if (!('Notification' in window) || Notification.permission !== 'granted') return;

    const unsubscribe = onForegroundMessage((payload) => {
      const toast: ToastData = {
        id: Date.now().toString(),
        title: payload.notification?.title || 'Romy',
        body: payload.notification?.body || '',
        clickAction: payload.data?.click_action || '/',
      };
      setToasts((prev) => [...prev, toast]);
      setTimeout(() => dismissToast(toast.id), 8000);
    });

    return unsubscribe;
  }, [currentUser, dismissToast]);

  // Native foreground messages + tap-to-navigate
  useEffect(() => {
    if (!currentUser || !Capacitor.isNativePlatform()) return;

    return addPushListeners(
      (path) => navigate(path),
      (notification) => {
        const toast: ToastData = {
          id: Date.now().toString(),
          title: notification.title,
          body: notification.body,
          clickAction: notification.clickAction,
        };
        setToasts((prev) => [...prev, toast]);
        setTimeout(() => dismissToast(toast.id), 8000);
      },
    );
  }, [currentUser, navigate, dismissToast]);

  const handleClick = (toast: ToastData) => {
    dismissToast(toast.id);
    navigate(toast.clickAction);
  };

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          onClick={() => handleClick(toast)}
          className="bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-pink-100 dark:border-pink-800 p-4 cursor-pointer hover:shadow-xl transition-shadow animate-in slide-in-from-right"
        >
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-pink-500 to-orange-500 flex items-center justify-center flex-shrink-0">
              <Bell size={16} className="text-white" weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-gray-800 dark:text-gray-100">{toast.title}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-2">{toast.body}</p>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                dismissToast(toast.id);
              }}
              className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
